import { html, nothing, type TemplateResult } from "lit";
import type { Engine } from "../../engine/Engine";
import type { Material } from "three";
import {
  applyMapSlot,
  applyTextureTransform,
  downloadTexture,
  listMapSlots,
  loadTextureFromFile,
  loadTextureFromUrl,
  texturePreviewSrc,
  type SceneDebugMapSlot,
} from "./sceneDebugModel";

export function renderSceneDebugMaterialMaps(opts: {
  material: Material;
  engine?: Engine;
  urlDrafts: Record<string, string>;
  onUrlDraft: (key: string, value: string) => void;
  onChanged: () => void;
}): TemplateResult {
  const slots = listMapSlots(opts.material);

  return html`
    <div class="sd-maps">
      ${slots.map((slot) => renderSlot(slot, opts))}
    </div>
  `;
}

function renderSlot(
  slot: SceneDebugMapSlot,
  opts: {
    material: Material;
    engine?: Engine;
    urlDrafts: Record<string, string>;
    onUrlDraft: (key: string, value: string) => void;
    onChanged: () => void;
  },
): TemplateResult {
  const tex = slot.texture;
  const preview = texturePreviewSrc(tex);
  const filled = !!tex;

  const replace = async (texture: Awaited<ReturnType<typeof loadTextureFromUrl>>) => {
    applyMapSlot(opts.material, slot, texture ?? null, opts.engine);
    opts.onChanged();
  };

  return html`
    <section class="sd-map ${filled ? "is-filled" : "is-empty"}">
      <header class="sd-map-head">
        <span class="sd-map-preview">
          ${preview
            ? html`<img src=${preview} alt="" />`
            : html`<span>${filled ? "tex" : "—"}</span>`}
        </span>
        <div class="sd-map-titles">
          <strong>${slot.label}</strong>
          <span>
            ${slot.kind}
            ${filled
              ? html` · ${tex!.name || tex!.uuid.slice(0, 8)}`
              : html` · empty`}
          </span>
        </div>
      </header>

      ${tex
        ? html`
            <div class="sd-map-xform">
              <label>
                repeat x
                <input
                  type="number"
                  step="0.01"
                  .value=${String(tex.repeat.x)}
                  @change=${(event: Event) => {
                    applyTextureTransform(
                      tex,
                      opts.material,
                      { repeatX: Number((event.currentTarget as HTMLInputElement).value) },
                      opts.engine,
                    );
                    opts.onChanged();
                  }}
                />
              </label>
              <label>
                repeat y
                <input
                  type="number"
                  step="0.01"
                  .value=${String(tex.repeat.y)}
                  @change=${(event: Event) => {
                    applyTextureTransform(
                      tex,
                      opts.material,
                      { repeatY: Number((event.currentTarget as HTMLInputElement).value) },
                      opts.engine,
                    );
                    opts.onChanged();
                  }}
                />
              </label>
              <label>
                offset x
                <input
                  type="number"
                  step="0.01"
                  .value=${String(tex.offset.x)}
                  @change=${(event: Event) => {
                    applyTextureTransform(
                      tex,
                      opts.material,
                      { offsetX: Number((event.currentTarget as HTMLInputElement).value) },
                      opts.engine,
                    );
                    opts.onChanged();
                  }}
                />
              </label>
              <label>
                offset y
                <input
                  type="number"
                  step="0.01"
                  .value=${String(tex.offset.y)}
                  @change=${(event: Event) => {
                    applyTextureTransform(
                      tex,
                      opts.material,
                      { offsetY: Number((event.currentTarget as HTMLInputElement).value) },
                      opts.engine,
                    );
                    opts.onChanged();
                  }}
                />
              </label>
              <label class="sd-check">
                <input
                  type="checkbox"
                  .checked=${tex.flipY}
                  @change=${(event: Event) => {
                    applyTextureTransform(
                      tex,
                      opts.material,
                      { flipY: (event.currentTarget as HTMLInputElement).checked },
                      opts.engine,
                    );
                    opts.onChanged();
                  }}
                />
                flipY
              </label>
            </div>
          `
        : nothing}

      <div class="sd-map-actions">
        <label class="sd-file">
          File
          <input
            type="file"
            accept="image/*,.ktx2,.ktx"
            @change=${async (event: Event) => {
              const file = (event.currentTarget as HTMLInputElement).files?.[0];
              (event.currentTarget as HTMLInputElement).value = "";
              if (!file) return;
              await replace(await loadTextureFromFile(file));
            }}
          />
        </label>
        <input
          class="sd-url"
          type="url"
          placeholder="Paste URL to replace this map"
          .value=${opts.urlDrafts[slot.key] ?? ""}
          @input=${(event: Event) =>
            opts.onUrlDraft(
              slot.key,
              (event.currentTarget as HTMLInputElement).value,
            )}
        />
        <button
          type="button"
          class="sd-btn"
          title="Replace this map from the URL field"
          @click=${async () => {
            const url = opts.urlDrafts[slot.key] ?? "";
            if (!url.trim()) return;
            await replace(await loadTextureFromUrl(url));
          }}
        >
          Apply URL
        </button>
      </div>
      <div class="sd-map-export">
        <button
          type="button"
          class="sd-btn sd-btn-ghost"
          ?disabled=${!filled}
          title="Download this map"
          @click=${async () => {
            if (!tex) return;
            await downloadTexture(tex, tex.name || slot.key);
          }}
        >
          Download
        </button>
        <button
          type="button"
          class="sd-btn sd-btn-ghost"
          ?disabled=${!filled}
          @click=${() => {
            applyMapSlot(opts.material, slot, null, opts.engine);
            opts.onChanged();
          }}
        >
          Clear
        </button>
      </div>
    </section>
  `;
}
